import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Truck, Save, LogOut, ExternalLink, Loader2, Upload, X,
  Plus, MapPin, Phone, Globe, Instagram, Building2
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Host-Hostes Firması", "Ses - Işık Firması", "Catering", "Transfer",
  "Seyahat Acentası", "Baskıcı", "Atölye", "Kurulum Ekibi", "Otel-Konaklama",
  "Fuar Lojistik", "Promosyon", "Bez-Kağıt Çanta", "Plaket", "Tasarım Ajansı",
  "Güvenlik", "Mobilya", "Ambulans", "Montaj", "Event Ajansı"
];

export default function SupplierDashboard() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const token = localStorage.getItem("supplier_token");

  useEffect(() => {
    if (!token) {
      navigate("/tedarikci-giris");
      return;
    }
    loadProfile();
  }, [token, navigate]);

  const loadProfile = async () => {
    try {
      const res = await axios.get(`${API}/supplier/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("supplier_token");
        navigate("/tedarikci-giris");
      }
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/supplier/me`, profile, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Profil kaydedildi!");
    } catch (err) {
      toast.error("Kaydetme hatası");
    } finally {
      setSaving(false);
    }
  };

  const compressImage = (file, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let w = img.width, h = img.height;
          if (w > maxWidth) { h = (maxWidth / w) * h; w = maxWidth; }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file, field === "cover_image" ? 1200 : 300, 0.8);
    setProfile({ ...profile, [field]: compressed });
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const currentGallery = profile.gallery || [];
    const remaining = 8 - currentGallery.length;
    if (remaining <= 0) {
      toast.error("Maksimum 8 görsel");
      return;
    }
    for (const file of files.slice(0, remaining)) {
      const compressed = await compressImage(file, 800, 0.7);
      setProfile((prev) => ({ ...prev, gallery: [...(prev.gallery || []), compressed] }));
    }
    e.target.value = "";
  };

  const toggleCategory = (cat) => {
    const categories = profile.categories || [];
    if (categories.includes(cat)) {
      setProfile({ ...profile, categories: categories.filter((c) => c !== cat) });
    } else {
      setProfile({ ...profile, categories: [...categories, cat] });
    }
  };

  const logout = () => {
    localStorage.removeItem("supplier_token");
    localStorage.removeItem("supplier_slug");
    navigate("/");
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-stone-300" /></div>;
  if (!profile) return null;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 font-serif">Tedarikçi Paneli</h1>
            <p className="text-sm text-stone-500">{profile.company_name}</p>
          </div>
          <div className="flex gap-2">
            {profile.is_published && profile.slug && (
              <Link to={`/tedarikci/${profile.slug}`} target="_blank">
                <Button variant="outline" size="sm" className="gap-1.5">
                  <ExternalLink className="w-4 h-4" /> Profili Gör
                </Button>
              </Link>
            )}
            <Button onClick={logout} variant="ghost" size="sm" className="gap-1.5 text-stone-500">
              <LogOut className="w-4 h-4" /> Çıkış
            </Button>
          </div>
        </div>

        {/* Profile Form */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-6">
          {/* Cover & Logo */}
          <div className="relative">
            <div className="h-32 bg-gradient-to-r from-[#1B4F72] to-[#2E86AB] rounded-lg overflow-hidden">
              {profile.cover_image && <img src={profile.cover_image} alt="" className="w-full h-full object-cover" />}
              <label className="absolute bottom-2 right-2 cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, "cover_image")} />
                <span className="bg-white/90 text-stone-700 text-xs px-2 py-1 rounded flex items-center gap-1">
                  <Upload className="w-3 h-3" /> Kapak
                </span>
              </label>
            </div>
            <div className="absolute -bottom-8 left-6">
              <div className="w-20 h-20 rounded-xl border-4 border-white bg-white shadow-sm overflow-hidden flex items-center justify-center">
                {profile.logo_url ? (
                  <img src={profile.logo_url} alt="" className="w-full h-full object-contain" />
                ) : (
                  <Truck className="w-8 h-8 text-stone-300" />
                )}
              </div>
              <label className="absolute bottom-0 right-0 cursor-pointer bg-[#1B4F72] text-white p-1 rounded-full">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, "logo_url")} />
                <Upload className="w-3 h-3" />
              </label>
            </div>
          </div>

          <div className="pt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-stone-500">Firma Adı</Label>
              <Input value={profile.company_name || ""} onChange={(e) => setProfile({ ...profile, company_name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">E-posta</Label>
              <Input value={profile.email || ""} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-stone-500">Açıklama</Label>
              <Textarea value={profile.description || ""} onChange={(e) => setProfile({ ...profile, description: e.target.value })} rows={3} placeholder="Firmanızı tanıtın..." className="mt-1" />
            </div>
          </div>

          <Separator />

          {/* Categories */}
          <div>
            <Label className="text-xs text-stone-500 mb-2 block">Hizmet Kategorileri</Label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => {
                const isSelected = (profile.categories || []).includes(cat);
                return (
                  <button
                    key={cat}
                    onClick={() => toggleCategory(cat)}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      isSelected
                        ? "bg-[#1B4F72] text-white border-[#1B4F72]"
                        : "bg-white text-stone-600 border-stone-200 hover:border-[#1B4F72]"
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>

          <Separator />

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-stone-500">Şehir</Label>
              <Input value={profile.city || ""} onChange={(e) => setProfile({ ...profile, city: e.target.value })} placeholder="İstanbul" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Telefon</Label>
              <Input value={profile.phone || ""} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="+90 5XX XXX XX XX" className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-stone-500">Adres</Label>
              <Input value={profile.address || ""} onChange={(e) => setProfile({ ...profile, address: e.target.value })} placeholder="Tam adres..." className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Website</Label>
              <Input value={profile.website || ""} onChange={(e) => setProfile({ ...profile, website: e.target.value })} placeholder="https://..." className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Instagram</Label>
              <Input value={profile.instagram || ""} onChange={(e) => setProfile({ ...profile, instagram: e.target.value })} placeholder="@kullanici" className="mt-1" />
            </div>
          </div>

          <Separator />

          {/* Services */}
          <div>
            <Label className="text-xs text-stone-500">Hizmetler (virgülle ayırın)</Label>
            <Input
              value={(profile.services || []).join(", ")}
              onChange={(e) => setProfile({ ...profile, services: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              placeholder="Catering, Lojistik, Ses Sistemi..."
              className="mt-1"
            />
          </div>

          <Separator />

          {/* Gallery */}
          <div>
            <Label className="text-xs text-stone-500 mb-2 block">Galeri (max 8 görsel)</Label>
            <div className="grid grid-cols-4 gap-2">
              {(profile.gallery || []).map((img, i) => (
                <div key={i} className="aspect-square relative border rounded overflow-hidden group">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => setProfile({ ...profile, gallery: profile.gallery.filter((_, j) => j !== i) })}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {(profile.gallery || []).length < 8 && (
                <label className="aspect-square border-2 border-dashed border-stone-300 rounded flex items-center justify-center cursor-pointer hover:border-[#1B4F72]">
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handleGalleryUpload} />
                  <Plus className="w-6 h-6 text-stone-400" />
                </label>
              )}
            </div>
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={profile.is_published || false}
                onChange={(e) => setProfile({ ...profile, is_published: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm text-stone-700">Profili Yayınla</span>
            </label>
            <Button onClick={saveProfile} disabled={saving} className="bg-[#1B4F72] hover:bg-[#154360] gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Kaydet
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
